import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys } from "@/api/queryClient";
import type { ConnectorAccessRequest } from "@/types";

function useClient() {
  const { session, logout } = useAuth();
  return () => createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
}

// Eine Entscheidung ändert BEIDE Sichten: die Antragsliste und den Enablement-Zustand des
// Agents (approve schaltet den Connector frei) — deshalb wird auch `agents` invalidiert.
function useInvalidateDecision() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.resource("connectors") });
    void qc.invalidateQueries({ queryKey: queryKeys.list("connector-access-requests") });
    void qc.invalidateQueries({ queryKey: queryKeys.resource("agents") });
  };
}

export function useApproveAccessRequest(): UseMutationResult<ConnectorAccessRequest, Error, string> {
  const getClient = useClient();
  const invalidate = useInvalidateDecision();
  return useMutation({
    mutationFn: (id) => getClient().post<ConnectorAccessRequest>(`/connector-access-requests/${id}/approve`),
    onSuccess: invalidate,
  });
}

export function useRejectAccessRequest(): UseMutationResult<ConnectorAccessRequest, Error, string> {
  const getClient = useClient();
  const invalidate = useInvalidateDecision();
  return useMutation({
    mutationFn: (id) => getClient().post<ConnectorAccessRequest>(`/connector-access-requests/${id}/reject`),
    onSuccess: invalidate,
  });
}

/** Zurückziehen (Antragsteller) ODER Entziehen einer erteilten Freigabe (Owner/Admin). */
export function useRevokeAccessRequest(): UseMutationResult<void, Error, string> {
  const getClient = useClient();
  const invalidate = useInvalidateDecision();
  return useMutation({
    mutationFn: (id) => getClient().delete<void>(`/connector-access-requests/${id}`),
    onSuccess: invalidate,
  });
}
